/**
 * A pill switch — the product's on/off control for a PREFERENCE.
 *
 * The reference's input catalogue has named `inputs/Toggle.tsx` all along and nothing had
 * needed one until the create modal's "add more" (16 Oct 2026), so this is that file rather
 * than a new idea: the name and the folder are the reference's.
 *
 * **It is for a preference, never for a value the record stores.** A yes/no the SERVER keeps
 * is `<RadioGroup>` Yes/No — `frontend-forms.md` says why, and the reason is exactly why a
 * switch is right here: a switch has no unanswered state, so it cannot express "nobody said".
 * That makes it wrong for a stored answer and right for a setting whose default IS the answer.
 *
 * **A real `<button role="switch">`**, so Space and Enter work, the base `cursor-pointer` rule
 * applies, and it joins the tab run on its own. It is deliberately NOT a `<Button>`: inside a
 * modal footer `<Button>` reads `ModalFooterSurfaceContext` and drops itself to `tabIndex={-1}`
 * (that is how Cancel and Autogenerate leave the run), and a control the reader operates must
 * not be pointer-only — WCAG 2.1.1.
 */
import { useId } from 'react';

/*
  Track, knob and travel per size — ONE map, written out whole (a class built from a template generates
  no CSS). The ON travel is the track's inner width (less its 1px borders) minus the knob, minus the 2px
  inset the OFF position keeps: md 34 − 14 − 2 = 18, lg 46 − 20 − 2 = 24.
*/
const SIZES = {
  md: { track: 'h-5 w-9', knob: 'h-3.5 w-3.5', on: 'translate-x-[18px]' },
  lg: { track: 'h-7 w-12', knob: 'h-5 w-5', on: 'translate-x-[24px]' },
} as const;

export function Toggle({
  checked,
  onChange,
  label,
  id,
  name,
  disabled = false,
  size = 'md',
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Visible, and the control's accessible name. A switch with no label states nothing. */
  label: string;
  id?: string;
  name?: string;
  disabled?: boolean;
  /**
   * `md` (20px track) by default. `lg` (28px) where the switch IS the banner's one decision —
   * Deliberate's Shortlist (product call, 20 Oct 2026). Track, knob and travel move together in `SIZES`.
   */
  size?: keyof typeof SIZES;
}) {
  const s = SIZES[size];
  const fallbackId = useId();
  const switchId = id ?? name ?? fallbackId;
  const labelId = `${switchId}-label`;

  return (
    <span className="inline-flex items-center gap-2">
      <button
        type="button"
        role="switch"
        id={switchId}
        name={name}
        aria-checked={checked}
        aria-labelledby={labelId}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={[
          `relative inline-flex ${s.track} shrink-0 items-center rounded-full border transition`,
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300',
          'disabled:cursor-not-allowed disabled:opacity-40',
          /*
            THE TRACK CARRIES THE 3:1, NOT THE KNOB — 1.4.11 is about the control being
            distinguishable from what is around it, and the knob is white on both states.

            OFF is the `<Checkbox>` idiom exactly: a pale ground with a `slate-500` BORDER
            doing the contrast work, because `slate-300` is 1.5:1 on white and fails. ON is
            `brand-700` (3.42:1), the same step the checkbox tick uses and for the same
            reason — `brand-500` is 2.16:1 and nearly indistinguishable from off at this size.
          */
          checked
            ? 'border-brand-700 bg-brand-700'
            : 'border-slate-500 bg-slate-200',
        ].join(' ')}
      >
        <span
          aria-hidden="true"
          className={[
            `inline-block ${s.knob} rounded-full bg-white shadow-sm transition-transform`,
            checked ? s.on : 'translate-x-[2px]',
          ].join(' ')}
        />
      </button>

      {/*
        A `<label htmlFor>` does not drive a `<button>` — only a form control. So the text is a
        plain span wired by `aria-labelledby`, and it carries its own click handler to keep the
        label clickable, which is what a reader expects of a switch's own words.
      */}
      <span
        id={labelId}
        onClick={() => !disabled && onChange(!checked)}
        className={[
          'select-none text-sm',
          disabled ? 'cursor-not-allowed text-slate-400' : 'cursor-pointer text-slate-600',
        ].join(' ')}
      >
        {label}
      </span>
    </span>
  );
}
